import { Clarification } from '@/types/clarification';
import {
  getDependencyChain,
  isQuestionVisibleTransitive,
} from '@/hooks/submission_forms/questionVisibility';
import { FormFieldValue, FormTreeData, QuestionData } from './types';

/** One stop for the clarification navigator: a visible question with open threads. */
export interface ClarificationTarget {
  questionSlug: string;
  questionPrompt: string;
  /** Depth-0 FormTreeData.id — the collapsible card the question renders in */
  topLevelSectionId: string;
  topLevelSectionLabel: string;
  unresolvedCount: number;
}

export type StepDirection = 'next' | 'prev';

/**
 * Visit every question in the order FormGroup renders them: a node's own
 * questions first, then its children. `topLevelSection` is the depth-0 node
 * the question sits under, however deeply nested.
 */
export const forEachQuestionInRenderOrder = (
  formTree: FormTreeData[],
  visit: (question: QuestionData, topLevelSection: FormTreeData) => void
): void => {
  const walk = (node: FormTreeData, topLevelSection: FormTreeData) => {
    node.questions.forEach(question => visit(question, topLevelSection));
    node.children?.forEach(child => walk(child, topLevelSection));
  };
  formTree.forEach(section => walk(section, section));
};

export const countUnresolved = (clarifications: Clarification[] = []): number =>
  clarifications.filter(c => !c.resolved).length;

/** Slugs of questions that have at least one unresolved root clarification. */
export const slugsWithUnresolved = (
  clarificationsByQuestion: Record<string, Clarification[]>
): string[] =>
  Object.entries(clarificationsByQuestion)
    .filter(([, clarifications]) => countUnresolved(clarifications) > 0)
    .map(([slug]) => slug);

/**
 * Every question slug whose answer can change the visibility of one of
 * `slugs` — i.e. the form values that must be watched to keep the target
 * list in step with branching.
 */
export const visibilityDependencySlugs = (
  slugs: string[],
  questionsBySlug: ReadonlyMap<string, QuestionData>
): string[] => {
  const dependencies = new Set<string>();
  slugs.forEach(slug => {
    const question = questionsBySlug.get(slug);
    if (!question) return;
    getDependencyChain(question, questionsBySlug).forEach(link => {
      if (link.depends_on) dependencies.add(link.depends_on.question_slug);
    });
  });
  return Array.from(dependencies).sort();
};

/**
 * Ordered navigation targets: one per question that is currently visible
 * (branching applied) and has at least one unresolved root clarification.
 * Several threads on one question share a dialog, so they are one stop.
 */
export const buildClarificationTargets = (
  formTree: FormTreeData[],
  clarificationsByQuestion: Record<string, Clarification[]>,
  formValues: Record<string, FormFieldValue>,
  questionsBySlug: ReadonlyMap<string, QuestionData>
): ClarificationTarget[] => {
  const targets: ClarificationTarget[] = [];
  forEachQuestionInRenderOrder(formTree, (question, topLevelSection) => {
    const unresolvedCount = countUnresolved(
      clarificationsByQuestion[question.slug]
    );
    if (unresolvedCount === 0) return;
    if (!isQuestionVisibleTransitive(question, formValues, questionsBySlug)) {
      return;
    }
    targets.push({
      questionSlug: question.slug,
      questionPrompt: question.prompt,
      topLevelSectionId: topLevelSection.id,
      topLevelSectionLabel: topLevelSection.label,
      unresolvedCount,
    });
  });
  return targets;
};

/** Question slug → position in render order, for locating non-target questions. */
export const buildRenderOrderIndex = (
  formTree: FormTreeData[]
): Map<string, number> => {
  const order = new Map<string, number>();
  forEachQuestionInRenderOrder(formTree, question => {
    order.set(question.slug, order.size);
  });
  return order;
};

/**
 * Where `slug` sits relative to the target list:
 * - a target's own index when `slug` is a target;
 * - `k - 0.5` when it falls between targets k-1 and k (e.g. the question just
 *   had its last thread resolved, so it dropped out of the list);
 * - `-0.5` when there is no position yet.
 * Stepping from a half-position lands on the neighbour in that direction.
 */
export const cursorFor = (
  targets: ClarificationTarget[],
  slug: string | null,
  renderOrder: ReadonlyMap<string, number>
): number => {
  if (slug === null) return -0.5;
  const index = targets.findIndex(t => t.questionSlug === slug);
  if (index !== -1) return index;
  const position = renderOrder.get(slug);
  if (position === undefined) return -0.5;
  const before = targets.filter(
    t => (renderOrder.get(t.questionSlug) ?? -1) < position
  ).length;
  return before - 0.5;
};

/** Index to move to from `cursor`, wrapping at both ends. -1 when empty. */
export const stepIndex = (
  cursor: number,
  length: number,
  direction: StepDirection
): number => {
  if (length === 0) return -1;
  if (direction === 'next') {
    const next = Math.floor(cursor) + 1;
    return next >= length ? 0 : next;
  }
  const prev = Math.ceil(cursor) - 1;
  return prev < 0 ? length - 1 : prev;
};

/** Copy for unresolved threads the navigator can't reach (see hiddenUnresolvedCount). */
export const hiddenOpenText = (count: number): string =>
  `${count} open on ${count === 1 ? 'a hidden question' : 'hidden questions'}`;
